import { ExecutionStep, StepType } from '../types/rust-engine-interfaces';
import { step, genericPayload } from './fallback-helpers';

interface Pt { x: number; y: number; }

export function geometryFallback(id: string, raw: any): ExecutionStep[] {
  const txt = id.toLowerCase();
  if (txt.includes('convex') || txt.includes('hull') || txt.includes('graham') || txt.includes('jarvis')) return convexHull(raw);
  if (txt.includes('closest') || txt.includes('pair')) return closestPair(raw);
  if (txt.includes('sweep') || txt.includes('line-sweep')) return lineSweep(raw);
  if (txt.includes('voronoi')) return voronoi(raw);
  return convexHull(raw);
}

function defaultPoints(raw: any): Pt[] {
  const pts: any[] = (raw?.points || [
    { x: 0, y: 0 }, { x: 1, y: 3 }, { x: 3, y: 1 }, { x: 3, y: 3 },
    { x: 5, y: 0 }, { x: 2, y: 2 }, { x: 4, y: 4 }, { x: 1, y: 1 },
  ]).slice(0, 12);
  return pts.map(p => ({ x: p.x, y: p.y }));
}

function toGraphElements(pts: Pt[], activeIdxs: number[] = []) {
  return pts.map((p, i) => ({
    id: `p-${i}`,
    type: 'node' as const,
    label: `P${i}`,
    value: `(${p.x},${p.y})`,
    position: { x: 100 + p.x * 80, y: 300 - p.y * 60 },
    style: {
      fillColor: activeIdxs.includes(i) ? '#f59e0b' : '#2563eb',
      strokeColor: activeIdxs.includes(i) ? '#fbbf24' : '#60a5fa',
      strokeWidth: 2,
    },
  }));
}

function convexHull(raw: any): ExecutionStep[] {
  const pts = defaultPoints(raw);
  const n = pts.length;
  if (n < 3) return [step(0, StepType.Completion, genericPayload([], { points: pts }), { line: 1, explanation: 'Convex Hull: need at least 3 points.' })];

  const s: ExecutionStep[] = [];
  s.push(step(s.length, StepType.Initialization, genericPayload(toGraphElements(pts), { points: pts }), { line: 1, explanation: `Convex Hull (Graham Scan): ${n} points. Find the convex hull.` }));

  // Find bottom-most point
  let bottom = 0;
  for (let i = 1; i < n; i++) if (pts[i]!.y < pts[bottom]!.y) bottom = i;
  s.push(step(s.length, StepType.Comparison, genericPayload(toGraphElements(pts, [bottom]), { points: pts, hull: [bottom] }), { line: 3, explanation: `Lowest point: P${bottom} at (${pts[bottom]!.x},${pts[bottom]!.y}). Anchor for polar sort.` }));

  // Sort by polar angle from bottom point
  const sorted = pts.map((p, i) => ({ i, p })).filter(x => x.i !== bottom);
  sorted.sort((a, b) => {
    const angA = Math.atan2(a.p.y - pts[bottom]!.y, a.p.x - pts[bottom]!.x);
    const angB = Math.atan2(b.p.y - pts[bottom]!.y, b.p.x - pts[bottom]!.x);
    return angA - angB;
  });

  const hull: number[] = [bottom];
  for (const { i } of sorted) {
    s.push(step(s.length, StepType.Comparison, genericPayload(toGraphElements(pts, [i, ...hull]), { points: pts, hull }), { line: 5, explanation: `Consider P${i} at (${pts[i]!.x},${pts[i]!.y}).` }));
    while (hull.length >= 2) {
      const a = hull[hull.length - 2]!;
      const b = hull[hull.length - 1]!;
      const cross = (pts[b]!.x - pts[a]!.x) * (pts[i]!.y - pts[a]!.y) - (pts[b]!.y - pts[a]!.y) * (pts[i]!.x - pts[a]!.x);
      if (cross <= 0) {
        hull.pop();
        s.push(step(s.length, StepType.Decision, genericPayload(toGraphElements(pts, hull), { points: pts, hull }), { line: 7, explanation: `Right turn at P${b}. Pop from hull.` }));
      } else break;
    }
    hull.push(i);
    s.push(step(s.length, StepType.Update, genericPayload(toGraphElements(pts, hull), { points: pts, hull }), { line: 8, explanation: `Add P${i} to hull. Hull: [${hull.map(h => 'P' + h).join(' → ')}].` }));
  }

  s.push(step(s.length, StepType.Completion, genericPayload(toGraphElements(pts, hull), { points: pts, hull }), { line: 10, explanation: `Convex Hull complete: ${hull.length} vertices.` }));
  return s;
}

function closestPair(raw: any): ExecutionStep[] {
  const pts = defaultPoints(raw).slice(0, 8);
  const n = pts.length;
  const s: ExecutionStep[] = [];
  s.push(step(s.length, StepType.Initialization, genericPayload(toGraphElements(pts), { points: pts }), { line: 1, explanation: `Closest Pair of Points: ${n} points. Find the pair with minimum distance.` }));

  let minDist = Infinity;
  let minPair: [number, number] = [0, 1];

  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const d = Math.hypot(pts[i]!.x - pts[j]!.x, pts[i]!.y - pts[j]!.y);
      s.push(step(s.length, StepType.Comparison, genericPayload(toGraphElements(pts, [i, j]), { points: pts, pair: [i, j], dist: d.toFixed(2) }), { line: 3, explanation: `Compare P${i} and P${j}: distance=${d.toFixed(2)}.` }));
      if (d < minDist) {
        minDist = d;
        minPair = [i, j];
        s.push(step(s.length, StepType.Update, genericPayload(toGraphElements(pts, [i, j]), { points: pts, pair: [i, j], dist: d.toFixed(2) }), { line: 5, explanation: `New minimum: P${i} ↔ P${j}, dist=${d.toFixed(2)}.` }));
      }
    }
  }

  s.push(step(s.length, StepType.Completion, genericPayload(toGraphElements(pts, minPair), { points: pts, pair: minPair, dist: minDist.toFixed(2) }), { line: 7, explanation: `Closest pair: P${minPair[0]} and P${minPair[1]}, distance=${minDist.toFixed(2)}.` }));
  return s;
}

function lineSweep(raw: any): ExecutionStep[] {
  const pts = defaultPoints(raw).slice(0, 8);
  const sorted = pts.map((p, i) => ({ p, i })).sort((a, b) => a.p.x - b.p.x);
  const s: ExecutionStep[] = [];
  s.push(step(s.length, StepType.Initialization, genericPayload(toGraphElements(pts), { points: pts }), { line: 1, explanation: `Line Sweep: ${pts.length} points. Sort by x-coordinate and sweep left to right.` }));

  for (let si = 0; si < sorted.length; si++) {
    const { p, i } = sorted[si]!;
    s.push(step(s.length, StepType.Comparison, genericPayload(toGraphElements(pts, [i]), { points: pts, sweepX: p.x, processed: si + 1 }), { line: 3, explanation: `Sweep at x=${p.x}: process P${i} at (${p.x},${p.y}).` }));
  }

  s.push(step(s.length, StepType.Completion, genericPayload(toGraphElements(pts, sorted.map(x => x.i)), { points: pts }), { line: 5, explanation: 'Line Sweep complete. All points processed.' }));
  return s;
}

function voronoi(raw: any): ExecutionStep[] {
  const pts = defaultPoints(raw).slice(0, 6);
  const n = pts.length;
  const s: ExecutionStep[] = [];
  s.push(step(s.length, StepType.Initialization, genericPayload(toGraphElements(pts), { points: pts }), { line: 1, explanation: `Voronoi Diagram (simplified): ${n} sites. Each region = closest site.` }));

  for (let i = 0; i < n; i++) {
    s.push(step(s.length, StepType.Comparison, genericPayload(toGraphElements(pts, [i]), { points: pts, site: i }), { line: 3, explanation: `Site P${i} at (${pts[i]!.x},${pts[i]!.y}): compute Voronoi cell.` }));
  }

  s.push(step(s.length, StepType.Completion, genericPayload(toGraphElements(pts), { points: pts, regions: n }), { line: 5, explanation: `Voronoi: ${n} regions computed.` }));
  return s;
}
